// pineSecurityContext — nối runtime `request.security` (P3.3-IMPL) vào host panels.
//
// Thiết kế P3.3-HOST (PLAN audit R1/R2): KHÔNG gọi mạng mới. HTF bars được
// TỔNG HỢP từ plotData của chart đang active (bucket floor-epoch, chỉ giữ
// bucket ĐÃ ĐÓNG) — đúng hướng no-lookahead của IMPL (mapping T+htfMs ≤ t,
// host date = OPEN time). Real-fetched HTF bars (host MTF fetch) sẽ cần cho
// cross-symbol — non-goal của slice này (đã ghi ở design amend).
//
// TF naming cẩn trọng (AUDIT R1-O1, đã đo): `historyDataUtils.resolveTimeframeMs`
// THIẾU `1H/2H/4H/D/W` (rơi default 24h) — helper này dùng bản regex của
// `lib/indicators/builtin/MTFWrapper` và tự pre-validate: tf lạ → null/bỏ.
// Helper pure: KHÔNG import widget-pro/chrome/demo. Security TF list đến từ
// adapter (`securityTfs`, pro cung cấp qua AST scan) — thiếu adapter ⇒ ctx
// không có security ⇒ script security vẫn fail-closed nêu tên (IMPL).
import { resolveTimeframeMs as regexResolveTfMs } from "../../lib/indicators/builtin/MTFWrapper";
import { exchangeOfSymbol, resolveMarketProfile } from "../../lib/core/trading/MarketRules";
/** C2 host meta: suy `mintick` + `currency` từ bars + symbol.
 * Thiếu bars/symbol/lastClose NaN ⇒ `{}` (host không set field, giữ fail-closed C1).
 * Limitation: ticker trần sàn HNX lấy tick HOSE (xem `exchangeOfSymbol`). */
export function pineMarketMeta(bars, symbol) {
    const closes = bars?.close;
    const lastClose = Array.isArray(closes) && closes.length > 0 ? closes[closes.length - 1] : NaN;
    if (!symbol || !symbol.trim() || !Number.isFinite(lastClose))
        return {};
    const rules = resolveMarketProfile(symbol);
    return { mintick: rules.getTickSize(lastClose, exchangeOfSymbol(symbol)), currency: rules.baseCurrency };
}
/** plotData (EnrichedDatum[]: OHLCV + date) → PineBars KÈM time. */
export function derivePineBars(rows) {
    if (!rows || rows.length === 0)
        return undefined;
    const open = [];
    const high = [];
    const low = [];
    const close = [];
    const volume = [];
    const time = [];
    for (const r of rows) {
        const date = r?.date;
        if (!date)
            return undefined;
        const t = date instanceof Date ? date.getTime() : Number(date);
        if (!Number.isFinite(t))
            return undefined;
        open.push(Number(r.open));
        high.push(Number(r.high));
        low.push(Number(r.low));
        close.push(Number(r.close));
        volume.push(Number(r.volume ?? 0));
        time.push(t);
    }
    return { open, high, low, close, volume, time };
}
/** Host TF ("1H","2H","4H","D","W", phút "5m"... cũng chấp nhận) → Pine TF; lạ → null. */
export function hostTfToPineTf(tf) {
    if (!tf)
        return null;
    const s = tf.trim();
    if (!/^\d{0,4}[mhdw]$/i.test(s))
        return null;
    const ms = regexResolveTfMs(s);
    // Phút dùng unit 'm' CHỮ THƯỜNG ("5m"); tháng là "M"/"1M" HOA (Pine) — phân biệt
    // case ở đây vì bucket tháng ≠ floor 30d, không aggregate được.
    if (s === "M" || s === "1M" || s === "Y" || s === "1Y")
        return null;
    const minutes = Math.round(ms / 60000);
    if (minutes < 60)
        return String(minutes);
    if (minutes === 60)
        return "60";
    if (minutes === 120)
        return "120";
    if (minutes === 240)
        return "240";
    if (minutes === 1440)
        return "D";
    if (minutes === 10080)
        return "W";
    return null;
}
/** Pine TF ("1","60","D","W","1D"...) → host naming để tính ms; lạ/tháng → null. */
export function pineTfToHostTf(tf) {
    if (!tf)
        return null;
    const s = tf.trim();
    if (/^1?[dw]$/i.test(s))
        return s.toUpperCase().endsWith("D") ? "D" : "W";
    if (/^1?m$/i.test(s))
        return null; // tháng — không aggregate theo floor 30d
    if (/^\d+$/.test(s)) {
        const minutes = parseInt(s, 10);
        if (minutes <= 0)
            return null;
        switch (minutes) {
            case 1: return "1m";
            case 3: return "3m";
            case 5: return "5m";
            case 15: return "15m";
            case 30: return "30m";
            case 60: return "1H";
            case 120: return "2H";
            case 240: return "4H";
            default: return null;
        }
    }
    return null;
}
/**
 * Tổng hợp chart bars thành HTF bars ( chỉ bucket ĐÃ ĐÓNG).
 * time = bucketStart epoch ms — khớp mapping rule IMPL: T + htfMs ≤ t
 * chọn đúng bucket vừa đóng, bucket đang-forming không bao giờ lọt.
 */
export function aggregateHtfBars(chart, htfMs) {
    const t = chart.time ?? [];
    const n = t.length;
    const open = [];
    const high = [];
    const low = [];
    const close = [];
    const volume = [];
    const time = [];
    // Một forward pass: aggregate chạy; khi bar kế đổi bucket ⇒ bucket vừa qua
    // ĐÃ ĐÓNG → flush. Bucket cuối (đang-forming) không bao giờ được flush.
    let cur = -1;
    let o = NaN;
    let h = -Infinity;
    let l = Infinity;
    let v = 0;
    let c = NaN;
    for (let i = 0; i < n; i++) {
        const bucket = Math.floor(t[i] / htfMs) * htfMs;
        if (bucket !== cur) {
            if (cur !== -1) {
                open.push(o);
                high.push(h);
                low.push(l);
                close.push(c);
                volume.push(v);
                time.push(cur);
            }
            cur = bucket;
            o = chart.open[i];
            h = chart.high[i];
            l = chart.low[i];
            v = chart.volume[i] ?? 0;
        }
        else {
            h = Math.max(h, chart.high[i]);
            l = Math.min(l, chart.low[i]);
            v += chart.volume[i] ?? 0;
        }
        c = chart.close[i];
    }
    return { open, high, low, close, volume, time };
}
/**
 * Suy chart TF (Pine naming) từ median delta dương của bars — cùng ladder với
 * `scriptRegistry.compute` (đường restore không có label). Ngoài ladder → null
 * (TF tháng/bar lạ: fail-closed, không aggregate).
 */
export function deriveChartTfFromBars(bars) {
    const times = bars.time ?? [];
    const deltas = [];
    for (let i = 1; i < times.length; i++) {
        const d = times[i] - times[i - 1];
        if (d > 0)
            deltas.push(d);
    }
    deltas.sort((a, b) => a - b);
    const median = deltas.length > 0 ? deltas[Math.floor(deltas.length / 2)] : 0;
    const minutes = median > 0 ? Math.round(median / 60000) : 0;
    if (minutes === 60)
        return "60";
    if (minutes === 120)
        return "120";
    if (minutes === 240)
        return "240";
    if (minutes === 1440)
        return "D";
    if (minutes === 10080)
        return "W";
    if (minutes > 0 && minutes < 60)
        return String(minutes);
    return null;
}
/**
 * Bản DUY NHẤT của luật ratio-cap (WS4/R8): lọc `?`/không map, bỏ TF ≤ chart TF,
 * bỏ tỉ lệ > 24, aggregate bằng `aggregateHtfBars`. Cả `pineOptions` (host path)
 * và `scriptRegistry.compute` (plugin path) đều dùng hàm này.
 */
export function securityBarsForTimeframes(chartBars, chartTf, tfs) {
    const chtMs = regexResolveTfMs(pineTfToHostTf(chartTf) ?? "");
    const sec = {};
    for (const tf of tfs) {
        if (!tf || tf === "?")
            continue;
        const hostTf = pineTfToHostTf(tf);
        if (!hostTf)
            continue; // evaluator sẽ nêu tên tf không hỗ trợ
        const ms = regexResolveTfMs(hostTf);
        if (chtMs <= 0 || ms <= chtMs || ms / chtMs > 24)
            continue; // evaluator nêu tên (lower-TF / cap)
        sec[tf] = aggregateHtfBars(chartBars, ms);
    }
    return sec;
}
/**
 * Hoàn thiện options theo `source` chạy trên bars ĐÃ derived (panel giữ
 * pineBase của BottomPanelHost). Key security = literal TF NGUYÊN VĂN trong
 * script (IMPL lookup `ctx.security?.[tfRaw]`); tf không map / `'?'` / fail
 * ratio → bỏ (evaluator còn nêu tên lỗi thật). rỗng ⇒ KHÔNG đặt field security
 * (payload byte-identical cho script không security — REQ-304).
 */
export function pineOptions(source, bars, opts = {}) {
    const options = {};
    // Fallback WS4: label không suy được (vd. restore không label, bar tháng) nhưng bars
    // có time hợp lệ ⇒ suy TF từ bars để hai đường (panel/host + plugin/compute) đồng nhất.
    const chartTf = hostTfToPineTf(opts.timeframe) ?? (bars?.time ? deriveChartTfFromBars(bars) : null);
    if (chartTf)
        options.timeframe = chartTf;
    if (opts.symbol && opts.symbol.trim())
        options.symbol = opts.symbol;
    const meta = pineMarketMeta(bars, opts.symbol);
    if (meta.mintick !== undefined)
        options.mintick = meta.mintick;
    if (meta.currency !== undefined)
        options.currency = meta.currency;
    if (opts.external && Object.keys(opts.external).length > 0)
        options.securityExternal = { ...opts.external };
    if (opts.inputs && Object.keys(opts.inputs).length > 0)
        options.inputs = { ...opts.inputs };
    if (!bars || !chartTf)
        return { bars, options };
    const tfs = opts.adapter?.securityTfs?.(source) ?? [];
    if (tfs.length === 0)
        return { bars, options };
    const security = securityBarsForTimeframes(bars, chartTf, tfs);
    if (Object.keys(security).length > 0)
        options.security = security;
    return { bars, options };
}
/**
 * Hoàn thiện pineCtx cho script `source`:
 * - bars từ plotData (kèm time);
 * - options.security gom theo literal TF trong script (key giữ NGUYÊN VĂN —
 *   IMPL lookup `ctx.security?.[tfRaw]`); tf không map được → bỏ (evaluator
 *   vẫn nêu tên lỗi thật); rỗng ⇒ KHÔNG đặt field security (zero-diff payload
 *   cho script không security).
 */
export function buildPineCtx(source, plotData, opts = {}) {
    return pineOptions(source, derivePineBars(plotData), opts);
}
